import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  construirDocumento,
  construirPaleta,
  opcoesMockupDaUrl,
  type ConfigPlanner,
} from '@miolos/core';
import { CenaMockup, paginasDaCena } from '@/mockup/CenaMockup';
import { preCarregarImagem, urlsDeImagem } from '@/impressao/useImpressao';
import { BASE, urlDaImagem } from '@/lib/api';

/**
 * Página da imagem de divulgação — o que o Chromium do servidor fotografa.
 *
 * Irmã da página de impressão (`paginas/Impressao.tsx`) e com o mesmo cuidado: a
 * rota é pública (quem abre é o servidor, sem sessão), mas só serve o miolo de
 * quem tem o passe, e a página só levanta `__mockupPronto` depois de carregar as
 * imagens, esperar as fontes e pintar dois quadros — senão a foto sairia com a
 * capa faltando ou com a letra de reserva.
 *
 * Quando **não há** o que desenhar, ela levanta `__mockupErro` com o motivo: o
 * servidor para de esperar na hora, em vez de esperar um minuto para falhar igual.
 */
type JanelaDoMockup = {
  __mockupPronto?: boolean;
  __mockupErro?: string;
};

export function Mockup() {
  const { id } = useParams<{ id: string }>();
  const [parametros] = useSearchParams();
  const token = parametros.get('t') ?? '';
  const opcoes = useMemo(() => opcoesMockupDaUrl(parametros), [parametros]);

  const [miolo, setMiolo] = useState<{ nome: string; config: ConfigPlanner } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  /** O erro da tela é o mesmo recado que o servidor recebe. */
  useEffect(() => {
    if (erro) (window as unknown as JanelaDoMockup).__mockupErro = erro;
  }, [erro]);

  useEffect(() => {
    if (!id || !token) {
      setErro('Link da imagem incompleto.');
      return;
    }

    let cancelado = false;
    fetch(`${BASE}/api/impressao/${encodeURIComponent(id)}?t=${encodeURIComponent(token)}`)
      .then(async (resposta) => {
        if (!resposta.ok) throw new Error('O link da imagem expirou.');
        return (await resposta.json()) as { nome: string; config: ConfigPlanner };
      })
      .then((dados) => {
        if (!cancelado) setMiolo(dados);
      })
      .catch((falha: Error) => {
        if (!cancelado) setErro(falha.message);
      });

    return () => {
      cancelado = true;
    };
  }, [id, token]);

  const documento = useMemo(() => (miolo ? construirDocumento(miolo.config) : null), [miolo]);
  const paleta = useMemo(() => (miolo ? construirPaleta(miolo.config.cores) : null), [miolo]);

  useEffect(() => {
    if (!documento) return;
    let cancelado = false;

    const preparar = async () => {
      const janela = window as unknown as JanelaDoMockup;
      janela.__mockupPronto = false;

      await Promise.all(urlsDeImagem(documento).map((url) => preCarregarImagem(urlDaImagem(url))));

      if (typeof document !== 'undefined' && 'fonts' in document) {
        await document.fonts.ready.catch(() => undefined);
      }

      await new Promise<void>((resolver) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolver()));
      });

      if (!cancelado) janela.__mockupPronto = true;
    };

    void preparar();

    return () => {
      cancelado = true;
    };
  }, [documento]);

  if (erro) {
    return (
      <p className="p-8 text-sm text-muted-foreground" role="alert">
        {erro}
      </p>
    );
  }

  if (!documento || !paleta || !miolo) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden' }}>
      <CenaMockup
        config={miolo.config}
        paleta={paleta}
        paginas={paginasDaCena(documento, opcoes.cena)}
        opcoes={opcoes}
      />
    </div>
  );
}
